/**
 * Chart node (brief-as-publication P1).
 *
 * Carries **data, not a picture**. The tempting shortcut is to render an SVG server-side and embed
 * it as an `image` — rejected deliberately: a picture cannot be re-derived, re-checked against the
 * run that produced it, or re-rendered in a new theme, and it severs the numbers from their
 * provenance. Worse, it quietly breaks computed-never-asserted — a drawn chart is an assertion,
 * a rendered one is a view of recorded values.
 *
 * So the node stores the series, and the host renders. `source {runId, nodeId}` is not decoration:
 * it is which thinker produced these numbers, and it is what lets a reader click from a bar back to
 * the run step that measured it.
 *
 * `alt` follows the live-object convention — REST reads and full-text search stay valid where a
 * host cannot render the chart. A chart with no alt text is invisible to search and to a screen
 * reader, which is the same failure twice.
 *
 * INTERNAL PM file; never re-exported from `index.ts`.
 */

import type { NodeSpec } from 'prosemirror-model';

export const CHART_NODE = 'chart';

/** The kinds that earn their place — one per prose pattern the brief used to spell out. */
export type ChartKind = 'sparkline' | 'bars' | 'gauge' | 'provenance';

/**
 * A single plotted value. `value: null` is a HOLE — an unmeasured point — and must render as a gap,
 * never as zero and never interpolated. That distinction is the whole reason this node stores data.
 */
export interface ChartPoint {
  label: string;
  value: number | null;
  /** Optional supporting numbers, e.g. {n: 169, p: 0.075} for a pattern edge. */
  meta?: Record<string, number | string | null>;
  /** Optional per-point state, e.g. "dormant" vs "live" for a matcher. */
  state?: string | null;
}

export interface ChartSeries {
  name: string;
  points: ChartPoint[];
  /** Units for the axis label — "%", "pts", "USD". Null when the values are unitless. */
  unit?: string | null;
}

export interface ChartData {
  kind: ChartKind;
  title: string;
  /** Which thinker produced these numbers. Both fields required — provenance is not optional. */
  source: { runId: string; nodeId: string };
  series: ChartSeries[];
  /** One line, Posture A vocabulary. Describes what is shown, never what to do about it. */
  caption?: string | null;
}

export const chartNode: NodeSpec = {
  group: 'block',
  atom: true,
  draggable: true,
  attrs: {
    // The whole ChartData payload, stored as an object. Kept as one attr rather than exploded into
    // many so the markdown round-trip is a single JSON blob (see ProseMirrorBuilder.ApplyCharts).
    data: { default: null },
    // Plain-text fallback: what this chart says, for search, export and screen readers.
    alt: { default: '' },
    partId: { default: null },
  },
  parseDOM: [
    {
      tag: 'div[data-chart]',
      getAttrs: (dom) => {
        const el = dom as HTMLElement;
        const raw = el.getAttribute('data-chart') ?? '';
        let data: unknown = null;
        try {
          data = raw ? JSON.parse(raw) : null;
        } catch {
          // A malformed payload renders as its alt text rather than throwing the editor away.
          data = null;
        }
        return {
          data,
          alt: el.getAttribute('data-alt') ?? el.textContent ?? '',
          partId: el.getAttribute('data-part-id'),
        };
      },
    },
  ],
  toDOM: (node) => [
    'div',
    {
      // After a Yjs round-trip `data` may already BE a JSON string; stringifying it again
      // double-encodes. Pass strings through, stringify objects.
      'data-chart':
        typeof node.attrs.data === 'string'
          ? node.attrs.data
          : node.attrs.data
            ? JSON.stringify(node.attrs.data)
            : '',
      'data-alt': node.attrs.alt ?? '',
      ...(node.attrs.partId ? { 'data-part-id': node.attrs.partId } : {}),
      class: 'docit-chart',
    },
    // The alt text is the DOM content, so a host with no renderer still shows something true.
    node.attrs.alt ?? '',
  ],
};
